import { useCallback, useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api, fmtDateTime } from "@/lib/api";
import { useSync } from "@/lib/live";
import { RouteHistory } from "@/admin/RouteHistory";
import { DispatchAttention } from "@/admin/DispatchAttention";
import { ServiceHoursBar } from "@/admin/ServiceHoursBar";
import { DispatchSettings } from "@/admin/DispatchSettings";

const JAIPUR = [26.9124, 75.7873];
const STALE_MIN = 10;
const minsAgo = (iso) => (Date.now() - new Date(iso).getTime()) / 60000;
const isLive = (r) => r.online && r.location && minsAgo(r.location.at) <= STALE_MIN;
const icon = (name, live) => L.divIcon({
  className: "",
  html: `<div style="display:flex;align-items:center;gap:4px;transform:translate(-14px,-14px)"><div style="width:28px;height:28px;border-radius:50%;background:${live ? "#16A34A" : "#9CA3AF"};border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.35)"></div><span style="background:#0D3B2B;color:#fff;font:700 11px sans-serif;padding:2px 6px;border-radius:8px;white-space:nowrap">${name}</span></div>`,
});

const Fit = ({ pts }) => {
  const map = useMap();
  const key = pts.map((p) => p.join(",")).join("|");
  useEffect(() => { if (pts.length) map.fitBounds(pts, { padding: [40, 40], maxZoom: 15 }); }, [key]); // eslint-disable-line
  return null;
};

const Legend = ({ live, reqs }) => (
  <p className="flex flex-wrap gap-4 text-xs font-semibold text-[#4A6358]" data-testid="admin-live-legend">
    <span><span className="mr-1 inline-block h-3 w-3 rounded-full bg-[#DC2626]" />Unassigned requests ({reqs})</span>
    <span><span className="mr-1 inline-block h-3 w-3 rounded-full bg-[#16A34A]" />Online partners ({live})</span>
    <span><span className="mr-1 inline-block h-3 w-3 rounded-full bg-[#9CA3AF]" />Offline / not seen {STALE_MIN}+ min</span>
  </p>
);

export const AdminLive = ({ role }) => {
  const [rows, setRows] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [at, setAt] = useState(null);
  const tick = useCallback(() => Promise.all([api.get("/admin/live"), api.get("/admin/live/requests")])
    .then(([a, b]) => { setRows(a.data); setReqs(b.data); setAt(new Date()); }).catch(() => {}), []);
  useEffect(() => { tick(); const id = setInterval(tick, 60000); return () => clearInterval(id); }, [tick]);
  useSync(tick);
  const located = rows.filter((r) => r.location);
  const pts = [...located.map((r) => [r.location.lat, r.location.lng]), ...reqs.map((r) => [r.lat, r.lng])];
  return (
    <div className="space-y-4">
    <ServiceHoursBar />
    <DispatchSettings canEdit={role === "admin"} />
    <DispatchAttention />
    <Legend live={rows.filter(isLive).length} reqs={reqs.length} />
    <div className="grid gap-4 lg:grid-cols-[1fr_340px]">
      <div className="relative z-0 h-[560px] overflow-hidden rounded-2xl border border-[#D2E0D8]" data-testid="admin-live-map">
        <MapContainer center={JAIPUR} zoom={12} className="h-full w-full">
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
          <Fit pts={pts} />
          {reqs.map((r) => (
            <CircleMarker key={r.booking_id} center={[r.lat, r.lng]} radius={10} pathOptions={{ color: "#fff", weight: 3, fillColor: "#DC2626", fillOpacity: 1 }}>
              <Tooltip>{r.booking_id} · {r.date}<br />{r.scrap_types.join(", ")}<br />{r.dispatch.state === "offered" ? `Offered to ${r.dispatch.staff_name}` : r.dispatch.state === "admin" ? "No partner nearby: assign manually" : "Waiting"}</Tooltip>
            </CircleMarker>
          ))}
          {located.map((r) => (
            <Marker key={r.staff_id} position={[r.location.lat, r.location.lng]} icon={icon(r.name, isLive(r))}>
              <Popup><b>{r.name}</b><br />{r.mobile}<br />{r.online ? "Online" : "Offline"} · seen {fmtDateTime(r.location.at)}{r.next && <><br />Next: {r.next.booking_id} · ETA {r.next.eta}</>}</Popup>
            </Marker>
          ))}
          {located.filter((r) => r.next).map((r) => (
            <Polyline key={`n-${r.staff_id}`} positions={[[r.location.lat, r.location.lng], [r.next.lat, r.next.lng]]} pathOptions={{ color: "#2E9E6B", dashArray: "6 6" }} />
          ))}
        </MapContainer>
      </div>
      <div className="space-y-2" data-testid="admin-live-list">
        <p className="text-xs text-[#7B968B]">Live: updates instantly{at ? ` · last ${at.toLocaleTimeString("en-IN")}` : ""}. Partners send location every 15 s while online with the app open.</p>
        {rows.map((r) => {
          const live = isLive(r);
          return (
            <div key={r.staff_id} className="rounded-2xl border border-[#D2E0D8] bg-white p-3 text-sm" data-testid={`live-row-${r.staff_id}`}>
              <div className="flex items-center justify-between">
                <p className="font-bold text-[#0D3B2B]">{r.name}</p>
                <span className={`shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-bold ${live ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[#EAEFEA] text-[#7B968B]"}`} data-testid={`live-status-${r.staff_id}`}>
                  {live ? "Online" : !r.location ? "No location yet" : r.online ? `Seen ${Math.round(minsAgo(r.location.at))} min ago` : "Offline"}</span>
              </div>
              <p className="text-xs text-[#4A6358]">{r.mobile}{r.vehicle ? ` · ${r.vehicle}` : ""} · {r.open_jobs} open pickup{r.open_jobs === 1 ? "" : "s"}</p>
              {r.next && <p className="mt-1 text-xs">Next: <b>{r.next.booking_id}</b> · {r.next.trip_status || "Not started"} · ETA {r.next.eta}<br /><span className="text-[#7B968B]">{r.next.address}</span></p>}
              {r.location && <a href={`https://www.google.com/maps?q=${r.location.lat},${r.location.lng}`} target="_blank" rel="noreferrer" className="text-xs font-semibold text-[#2E9E6B] underline" data-testid={`live-gmaps-${r.staff_id}`}>Open in Google Maps</a>}
            </div>
          );
        })}
        {!rows.length && <p className="rounded-2xl bg-white p-6 text-center text-[#7B968B]">No active partners.</p>}
      </div>
    </div>
    <RouteHistory partners={rows} />
    </div>
  );
};
